/**
 * Client-side bridge to the @outlaw/payments WASM kernel.
 *
 * Built from `oss/payments/` (PRQ-10). The pre-built bundler-target
 * artifact lives at `./wasm/` and is committed alongside the source for
 * easy redeploy. To rebuild from source, run `oss/payments/build-and-vendor.sh`.
 *
 * # Why this exists
 *
 * JavaScript number math is wrong for money:
 *   `Math.round(1.005 * 100) / 100 === 1.00` (should be 1.01)
 * because `1.005 * 100` evaluates to `100.4999…` in f64.
 *
 * The WASM kernel stores money as i64 minor units at scale 4 (so $1.00
 * is exactly `10000`, no drift), then applies half-up rounding back to
 * cents. The result is exact.
 *
 * # Boundary contract
 *
 * `toTwoDecimals(n: number): number` — round any finite number to 2
 * decimals, half-up. Synchronous. Replaces the legacy
 * `Math.round(n * 100) / 100` anti-pattern that was duplicated in
 * `lib/util.tsx` and `lib/picos/numberFormat.ts`. Both of those now
 * delegate here.
 *
 * Non-finite inputs (NaN, ±Infinity) are returned as-is to match the
 * legacy contract (the old code produced NaN/Infinity too, never threw).
 *
 * # Pre-snap to scale 4
 *
 * The WASM kernel strictly rejects inputs that don't round-trip through
 * scale 4 (its "float-drift gate" — catches things like
 * `0.1 + 0.2 === 0.30000000000000004`). For DISPLAY-rounding purposes
 * we want a permissive contract, so we Math.round-snap the input to
 * scale 4 first. Drift at scale 10000 is far smaller than at scale 100,
 * so this pre-snap rarely changes the answer; the WASM does the
 * scale-4 → scale-2 step exactly.
 */

import {moneyRoundToCents} from './wasm/payments_wasm';

/**
 * Round a number to 2 decimal places, half-up. Replacement for the
 * legacy `Math.round(n * 100) / 100`. Synchronous; safe for display
 * code that needs an immediate value.
 *
 * Examples:
 * - `toTwoDecimals(1.005) === 1.01` (was 1.00 under the legacy bug)
 * - `toTwoDecimals(2.675) === 2.68` (was 2.67)
 * - `toTwoDecimals(1.234) === 1.23` (unchanged)
 * - `toTwoDecimals(NaN) === NaN` (passthrough, no throw)
 */
export function toTwoDecimals(n: number): number {
    if (!Number.isFinite(n)) return n;
    // Pre-snap to scale 4 absorbs small input drift (e.g. accumulated
    // float error from upstream JS math). Snapping at scale 10000 is
    // much more reliable than at scale 100 because drift at the larger
    // scale rarely crosses a rounding boundary.
    const scale4 = Math.round(n * 10000) / 10000;
    try {
        return moneyRoundToCents(scale4);
    } catch {
        // Defensive: WASM rejects values past the i64 safe scale-4 range
        // (~$9 × 10¹¹ — far beyond any plausible money amount). Fall
        // back to legacy behavior so a pathologically-large input
        // doesn't crash the UI.
        return Math.round(n * 100) / 100;
    }
}

// Re-export the raw WASM money primitives for callers that want exact
// scale-4 arithmetic (no rounding to cents). Use these in CALCULATIONS;
// use `toTwoDecimals` only at the DISPLAY boundary.
export {
    moneyCheckedAdd,
    moneyCheckedSubtract,
    moneyCheckedMultiplyByScalar,
    moneyCheckedNegate,
    moneyCheckedSum,
    moneyRoundToCents,
    feeBasisPoints,
} from './wasm/payments_wasm';

// Exact single-payment line allocation (largest remainder at scale 4).
// `lib/paymentAllocation` places the results in whole cents and wraps them
// with line state (OWA-974).
export {
    allocateProportional as wasmAllocateProportional,
    allocateFirstInFirstOut as wasmAllocateFirstInFirstOut,
    allocateExpensesFirst as wasmAllocateExpensesFirst,
    allocateExpensesFirstInFirstOut as wasmAllocateExpensesFirstInFirstOut,
} from './wasm/payments_wasm';
export type {AllocationResult as WasmAllocationResult} from './wasm/payments_wasm';
